import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
// #892 P0-006 — gate adminOnly nav items behind the user's role bag so
// admin affordances do not leak to client / manager / consultant
// signed-in sessions.
import { useAuthz, type SenviroRole } from '@/lib/auth/useAuthz';
import type { NavGroup as NavGroupConfig, NavItem } from './nav-config';

/**
 * #892 P0-006 — senviroRoles that may see `adminOnly` nav items.
 * Mirrors the `RequireRole` admin gates in router.tsx; keep these in
 * sync. A user without any of these roles gets the same nav as a
 * brand-new authenticated client.
 */
const ADMIN_ROLES: ReadonlyArray<SenviroRole> = [
  'partner_admin',
  'customer_admin',
  'platform_admin',
];

/**
 * Sidebar nav group — eyebrow-labelled flat list.
 *
 * Refactored from the W16-F1-S2 collapsible accordion to the canonical
 * shape in `sustevo-handbook/brand/APP_SHELL.md` § 6.3 (eyebrow labels,
 * never accordions — accordions hide structure; eyebrows surface it).
 *
 * Renders:
 *   - A mono uppercase eyebrow above the list (skipped for the first
 *     group on the sidebar so it sits flush under the brand band).
 *   - A flat `<ul>` of nav rows matching APP_SHELL.md § 6.2 +
 *     HARMONIZATION.md § 7.4 / § 8.4 exactly: 2×16px active vertical
 *     bar in `--color-blue-500`, Phosphor at 20px (chrome size) +
 *     advisory regular weight, `px-2 py-1.5 rounded-md text-sm
 *     font-medium`, `gap-2` between active-bar / icon / label.
 *
 * Empty groups (e.g. the `operations` group whose items only render
 * inside detail-page sub-navs) render nothing.
 */
export interface NavGroupProps {
  group: NavGroupConfig;
  /**
   * When `true`, the eyebrow label is suppressed — used for the
   * first group on the sidebar so it sits flush under the brand band.
   * Defaults to `false`.
   */
  suppressLabel?: boolean;
  /**
   * Map of `item.to` → unread / pending count, rendered as a trailing
   * badge on the matching nav row per APP_SHELL.md § 6.2. Used today
   * by the `/notifications` row to surface the unread count that
   * previously lived in a topbar bell.
   */
  trailingCountByRoute?: Readonly<Record<string, number>>;
}

export function NavGroup({
  group,
  suppressLabel = false,
  trailingCountByRoute,
}: NavGroupProps): JSX.Element | null {
  const { t } = useTranslation();
  // #892 P0-006 — read the user's role bag and filter out adminOnly
  // items the user can't actually visit. While the role lookup is in
  // flight (or fails), conservatively hide admin items: a brief flicker
  // is better than leaking the full admin nav to a non-admin session.
  const { senviroRoles, isLoading } = useAuthz();
  const isAdmin = useMemo(
    () => !isLoading && ADMIN_ROLES.some((r) => senviroRoles.includes(r)),
    [isLoading, senviroRoles],
  );
  const items = useMemo(
    () => group.items.filter((item) => !item.adminOnly || isAdmin),
    [group.items, isAdmin],
  );
  if (items.length === 0) return null;

  // PM flat-top + eyebrow-grouped pattern (handbook APP_SHELL.md § 6.3):
  // the first group renders as a flat `<ul>` directly at the nav's
  // padding (no extra wrapper); subsequent groups wrap in `mt-4 px-2`
  // for an extra 8px indent that visually distinguishes secondary
  // groups from primary nav. Matches PM lines 268-411.
  if (suppressLabel) {
    return (
      <ul className="space-y-0.5" data-group-id={group.id}>
        {items.map((item) => (
          <li key={item.to}>
            <NavRow
              item={item}
              trailingCount={trailingCountByRoute?.[item.to]}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="mt-5 px-2" data-group-id={group.id}>
      {/*
        Eyebrow label — polish-pass A.
        Was: `font-mono text-[10px] uppercase tracking-wider ink-400`.
        Now: title-case sans, medium weight, slightly larger and softer.
        Reads as a quiet section header instead of a brutalist all-caps
        marker; the wider visual space (`mt-5` instead of `mt-4`) does
        the structural separation work.
      */}
      <p className="px-2 text-[11px] font-medium tracking-tight text-[var(--color-ink-600)] dark:text-[var(--color-ink-400)]">
        {t(group.i18nKey as 'nav.groups.workspace', {
          defaultValue: group.i18nKey,
        })}
      </p>
      <ul className="mt-1.5 space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavRow
              item={item}
              trailingCount={trailingCountByRoute?.[item.to]}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface NavRowProps {
  item: NavItem;
  trailingCount?: number;
}

function NavRow({ item, trailingCount }: NavRowProps): JSX.Element {
  const { t } = useTranslation();
  const label = t(item.i18nKey as 'nav.dashboard', {
    defaultValue: item.i18nKey,
  });
  const showBadge = typeof trailingCount === 'number' && trailingCount > 0;
  const badgeLabel = trailingCount && trailingCount > 99 ? '99+' : trailingCount;
  return (
    <NavLink
      to={item.to}
      data-tour={item.tourId}
      className={({ isActive }) =>
        cn(
          // HARMONIZATION.md § 7.4 row "Sidebar nav row label" pins this at
          // `text-sm font-medium` with state colours from § 6.4. BRAND.md
          // § 3.6 token discipline — colours read the canonical ink scale
          // directly (light/dark pair) instead of the shadcn
          // `text-foreground` / `text-muted-foreground` / `bg-muted` aliases.
          'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium',
          isActive
            ? 'text-[var(--color-ink-900)] dark:text-[var(--color-ink-100)]'
            : 'text-[var(--color-ink-600)] hover:bg-[var(--color-ink-50)] hover:text-[var(--color-ink-900)] dark:text-[var(--color-ink-400)] dark:hover:bg-[var(--color-ink-700)] dark:hover:text-[var(--color-ink-100)]',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/*
            Active accent — polish-pass A. A thin 2×16px vertical bar
            replaces the 1.5×1.5 dot. Reads as a quiet left-edge rail
            against the row's icon rather than a floating dot, and
            stays out of the way when inactive (transparent slot keeps
            the layout stable so labels don't shift on active-change).
          */}
          <span
            aria-hidden="true"
            className={cn(
              'inline-block h-4 w-0.5 shrink-0 rounded-full',
              isActive ? 'bg-[var(--color-blue-500)]' : 'bg-transparent',
            )}
          />
          {/*
            HARMONIZATION.md § 8.4 + COMPONENTS.md § 4.1.1 — sidebar nav
            icons at `size={20}` in chrome (advisory weight via the
            shared <Icon> wrapper's defaults).
          */}
          <item.Icon
            aria-hidden="true"
            className="h-5 w-5 shrink-0"
          />
          <span className="flex-1">{label}</span>
          {showBadge && (
            <span
              data-testid={`nav-trailing-badge-${item.to}`}
              className="ml-auto rounded-full bg-[var(--color-blue-600)] px-1.5 py-0.5 text-[10px] font-medium text-white"
            >
              {badgeLabel}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/**
 * Active-route helper retained from the W16-F1-S4 wiring — longest-prefix
 * match over a flat list. Still useful for the legacy callers that care
 * about which group "owns" the current pathname, even though we no longer
 * expand/collapse groups based on it.
 */
export function findActiveGroupId(
  groups: readonly NavGroupConfig[],
  pathname: string,
): string | null {
  const flat = groups.flatMap((g) =>
    g.items.map((i) => ({ groupId: g.id, to: i.to })),
  );
  const sorted = [...flat].sort((a, b) => b.to.length - a.to.length);
  const match = sorted.find(
    (entry) => pathname === entry.to || pathname.startsWith(`${entry.to}/`),
  );
  return match?.groupId ?? null;
}

export function useActiveGroupId(
  groups: readonly NavGroupConfig[],
  pathname: string,
): string | null {
  return useMemo(() => findActiveGroupId(groups, pathname), [groups, pathname]);
}
